import { useMemo } from "react";
import { formatCurrency } from "utils";
import { QuoteDetail, QuoteTireDetail } from "features/quotation/types";
import { CostSettingInfo } from "features/mst_proc_fee/types/proc-fee";
import { MstItemDetailWheelDetail } from "features/mst_wheel/types";
import { EstQuoteDetailTire } from "features/car_model/types";
import sortQuoteDetailWheelsForDisplay from "features/quotation/utils/sortQuoteDetailWheelsForDisplay";

const DISABLED_TEXT = "なし" as const;
const ENABLED_TEXT = "する" as const;
const TAKE_OUT_DISABLED_TEXT = "しない" as const;

interface UseQuotationWheelDataParams {
  quotation: QuoteDetail | null;
  firstTireDetail: QuoteTireDetail | null;
  isWheelType: boolean;
}

/**
 * Custom hook for wheel-specific quotation data calculations
 * Handles options info, pricing, and wheel data formatting
 * @param params - Object containing quotation, firstTireDetail, and isWheelType
 * @returns Computed wheel-specific data
 */
const useQuotationWheelData = ({
  quotation,
  firstTireDetail,
  isWheelType,
}: UseQuotationWheelDataParams) => {
  // Options info for wheel type
  const optionsInfo = useMemo<CostSettingInfo | null>(() => {
    if (!isWheelType || !quotation) return null;

    return {
      replac_labor_cost: formatCurrency(quotation.replac_labor_cost),
      waste_tire_cost_flag: quotation.waste_tire_cost_flag === 1,
      waste_tire_cost: formatCurrency(quotation.waste_tire_cost),
      rubber_stem_valve_flag: quotation.rubber_stem_valve_flag === 1,
      rubber_stem_valve_cost: formatCurrency(quotation.rubber_stem_valve_cost),
      nitrogen_inflation_flag: quotation.nitrogen_inflation_flag === 1,
      nitrogen_inflation_cost: formatCurrency(quotation.nitrogen_inflation_cost),
      hub_rust_prevention_flag: quotation.hub_rust_prevention_flag === 1,
      hub_rust_prevention_cost: formatCurrency(quotation.hub_rust_prevention_cost),
      tire_puncture_flag: quotation.tire_puncture_flag,
      cashback_flag: !!firstTireDetail?.cb_tire_price || !!firstTireDetail?.cb_wheel_price,
      cashback_period:
        !firstTireDetail?.cb_tire_price && !firstTireDetail?.cb_wheel_price ? "期間外" : "期間中",
      rounding: Number(quotation.round_type),
      take_out: quotation.take_out_flag === 0 ? ENABLED_TEXT : TAKE_OUT_DISABLED_TEXT,
      take_out_flag: quotation.take_out_flag === 0,
      nut: quotation.nut_item_cd
        ? `${quotation.nut_option_name}\u00A0\u00A0${formatCurrency(quotation.nut_sale_price || 0)}円`
        : DISABLED_TEXT,
      nut_flag: !!quotation.nut_item_cd,
      lock_nut: quotation.lock_nut_item_cd
        ? `${quotation.locknut_option_name}\u00A0\u00A0${formatCurrency(quotation.locknut_sale_price || 0)}円`
        : DISABLED_TEXT,
      lock_nut_flag: !!quotation.lock_nut_item_cd,
      hub_ring: quotation.hub_ring_item_cd
        ? `${quotation.hubring_option_name}\u00A0\u00A0${formatCurrency(quotation.hubring_sale_price || 0)}円`
        : DISABLED_TEXT,
      hub_ring_flag: !!quotation.hub_ring_item_cd,
      wheel_coating: quotation.wcoating_item_cd
        ? `${quotation.wcoating_option_name}\u00A0\u00A0${formatCurrency(quotation.wcoating_sale_price || 0)}円`
        : DISABLED_TEXT,
      wheel_coating_flag: !!quotation.wcoating_item_cd,
    };
  }, [quotation, firstTireDetail, isWheelType]);

  // Calculate total wheel options price
  const wheelOptionsPrice = useMemo(() => {
    if (!isWheelType || !quotation) return 0;

    let total = 0;
    if (quotation.nut_item_cd) total += quotation.nut_sale_price || 0;
    if (quotation.lock_nut_item_cd) total += quotation.locknut_sale_price || 0;
    if (quotation.hub_ring_item_cd) total += quotation.hubring_sale_price || 0;
    if (quotation.wcoating_item_cd) total += quotation.wcoating_sale_price || 0;

    return total;
  }, [quotation, isWheelType]);

  // Format wheel data for display
  const wheelData = useMemo<Array<[Partial<MstItemDetailWheelDetail>, EstQuoteDetailTire[]]>>(() => {
    if (!isWheelType || !quotation || !quotation.tbl_quote_detail_wheel) return [];

    type WheelRow = [Partial<MstItemDetailWheelDetail>, EstQuoteDetailTire[]];

    const sortedWheels = sortQuoteDetailWheelsForDisplay(quotation.tbl_quote_detail_wheel);

    return sortedWheels.map((wheel): WheelRow => {
      const wheelDetail: Partial<MstItemDetailWheelDetail> = {
        midw_wheel_hub_diam: wheel.midw_wheel_hub_diam || "",
        fixed_discount: wheel.fixed_discount || 0,
        maker_name: wheel.maker_name || "",
        wheel_size: wheel.wheel_size || "",
        brand_name: wheel.brand_name || "",
        wheel_color_name: wheel.wheel_color_name || "",
        regular_price: wheel.sale_price,
        discounted_price: wheel.discounted_sale_price || 0,
        final_price: wheel.confirm_total_price || 0,
        wheel_image_url: wheel.image_url || "",
        remarks: wheel.remarks || "",
      };

      const wheelTire: EstQuoteDetailTire[] = wheel.tbl_quote_detail_tire.map((tire) => ({
        tire_item_cd: tire.tire_item_cd,
        maker_name: tire.maker_name || "",
        item_name: tire.item_name || "",
        tire_loin_speed_mark: tire.tire_loin_speed_mark || "",
        tire_label: tire.tire_label || "",
        quantity: tire.quantity || 0,
        nomal_price: tire.nomal_price || 0,
        fixed_discount: tire.fixed_discount || 0,
        special_discount: tire.special_discount || 0,
        discounted_sale_price: tire.discounted_sale_price || 0,
        wage_cost_price: tire.wage_cost_price || 0,
        wage_cost_in_price: tire.wage_cost_in_price || 0,
        puncture_protection_price: tire.puncture_protection_price || 0,
        puncture_protection_in_price: tire.puncture_protection_in_price || 0,
        cb_tire_price: tire.cb_tire_price || 0,
        cb_wheel_price: tire.cb_wheel_price || 0,
        confirm_total_price: tire.confirm_total_price || 0,
        remarks: tire.remarks || "",
        checked: true,
      }));

      return [wheelDetail, wheelTire];
    });
  }, [quotation, isWheelType]);

  // Rounding value for price calculations
  const roundingValue = useMemo(
    () => Number(quotation?.round_type ?? 0),
    [quotation?.round_type],
  );

  return {
    optionsInfo,
    wheelOptionsPrice,
    wheelData,
    roundingValue,
  };
};

export default useQuotationWheelData;

